import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { FieldErrors, FieldValues } from 'react-hook-form';
import { z } from 'zod';

export type FieldErrorFn<T extends FieldValues> = (key: keyof T, nested?: { index: number; key: string }) => string | undefined;

interface ReturnType<T extends FieldValues> {
  fieldError: FieldErrorFn<T>;
  nonFieldErrors: string[];
  isFieldRequired: (field: keyof T) => boolean;
  setGeneralError: Dispatch<SetStateAction<string | undefined>>;
}

/**
 * Use this hook for form validation. It will automatically surface zod errors.
 * Use the `fieldError` function to attach an error to an input field.
 * Use the `nonFieldErrors` for all generic errors.
 *
 * @param schema - The zod schema for the form
 * @param formErrors - The errors object from react-hook-form
 * @param ignoreFieldError - Fields to exclude from nonFieldErrors on first load (e.g. conditional or array fields)
 * @param conditionalValues - Current form values needed for conditional superRefine checks
 */
function useFormError<T extends FieldValues>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: z.ZodObject<any>,
  formErrors: FieldErrors<T>,
  ignoreFieldError?: (keyof T)[],
  conditionalValues?: Partial<T>,
): ReturnType<T> {
  const [requestedFields, setRequestedFields] = useState<(keyof T)[]>(ignoreFieldError ?? []);
  const [generalError, setGeneralError] = useState<string | undefined>();

  const fieldError = useCallback(
    (key: keyof T, nested?: { index: number; key: string }): string | undefined => {
      if (!requestedFields.includes(key)) {
        setRequestedFields(prev => [...prev, key]);
      }

      // Check nested field array errors first
      if (
        nested &&
        formErrors?.[key] &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        formErrors?.[key][nested.index] &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        formErrors?.[key][nested.index][nested.key] &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        formErrors?.[key][nested.index][nested.key]?.message
      ) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return formErrors?.[key]?.[nested.index]?.[nested.key]?.message?.toString() ?? '';
      } else if (formErrors && formErrors?.[key]?.message) {
        return formErrors?.[key]?.message?.toString() ?? '';
      }

      // Collect nested messages from arrays/objects
      const rootError = formErrors?.[key];
      if (rootError) {
        const collect = (fieldError: unknown, path = ''): string[] => {
          if (fieldError === null || fieldError === undefined) return [];

          if (typeof fieldError === 'object' && fieldError !== null) {
            const obj = fieldError as Record<string, unknown>;
            if ('message' in obj && typeof obj.message === 'string') {
              const prefix = path && path !== '' ? `${path}: ` : '';
              return [`${prefix}${obj.message}`];
            }
          }

          if (Array.isArray(fieldError)) {
            return fieldError.flatMap((el, i) => collect(el, `${path}[${i}]`));
          }

          if (typeof fieldError === 'object' && fieldError !== null) {
            return Object.entries(fieldError as Record<string, unknown>).flatMap(([k, v]) => collect(v, path ? `${path}.${k}` : k));
          }

          return [];
        };

        const msgs = collect(rootError, key.toString());
        if (msgs.length > 0) return msgs.join('\r\n');
      }

      return undefined;
    },
    [formErrors, requestedFields],
  );

  const nonFieldErrors = useMemo((): string[] => {
    const zodKeys = <T extends z.ZodTypeAny>(schema: T): string[] => {
      if (schema === null || schema === undefined) return [];
      if (schema instanceof z.ZodNullable || schema instanceof z.ZodOptional) return zodKeys(schema.unwrap() as z.ZodTypeAny);
      if (schema instanceof z.ZodArray) return zodKeys(schema.element as z.ZodTypeAny);
      if (schema instanceof z.ZodObject) {
        try {
          const shape = schema.shape;
          if (!shape) return [];
          const entries = Object.entries(shape);
          return entries.filter(([, value]) => value !== undefined).map(([key]) => key);
        } catch (error) {
          console.warn('Error getting zod keys:', error);
          return [];
        }
      }
      return [];
    };

    const messages: string[] = [];

    if (formErrors) {
      const schemaKeys = zodKeys(schema);
      for (const zodKey of schemaKeys) {
        const found = requestedFields.find(field => field === zodKey);
        if (!found && formErrors[zodKey]) {
          let errorText = formErrors?.[zodKey]?.message?.toString();

          if (errorText === undefined || requestedFields.includes(zodKey) === false) {
            errorText = 'Field or fieldError() not set for this field';
          }

          messages.push(`${zodKey.toString()}: ${errorText}`);
        }
      }
    }

    if (generalError) {
      messages.push(generalError);
    }

    return messages;
  }, [formErrors, schema, requestedFields, generalError]);

  const fieldResult = useMemo((): Map<string, boolean> => {
    const fieldResults = new Map<string, boolean>();

    try {
      const shape = schema.shape;
      if (!shape) return fieldResults;

      const fieldNames = Object.keys(shape);

      for (const fieldName of fieldNames) {
        try {
          const fieldSchema = shape[fieldName];
          const result = fieldSchema.safeParse(undefined);
          fieldResults.set(fieldName, !result.success);
        } catch (error) {
          console.warn(`Error checking field ${fieldName}:`, error);
          fieldResults.set(fieldName, false);
        }
      }

      try {
        const testObject: Record<string, unknown> = {};
        fieldNames.forEach(field => {
          testObject[field] = undefined;
        });

        if (conditionalValues) {
          Object.assign(testObject, conditionalValues);
        }

        const schemaResult = schema.safeParse(testObject);
        if (!schemaResult.success) {
          schemaResult.error.issues.forEach(issue => {
            if (issue.path.length === 1) {
              const fieldName = issue.path[0] as string;
              if (issue.code === 'custom') {
                fieldResults.set(fieldName, true);
              }
            }
          });
        }
      } catch (error) {
        console.warn('Error checking full schema:', error);
      }
    } catch (error) {
      console.warn('Error accessing schema.shape:', error);
    }

    return fieldResults;
  }, [schema, conditionalValues]);

  const isFieldRequired = useCallback(
    (field: keyof T): boolean => {
      return fieldResult.get(field as string) ?? false;
    },
    [fieldResult],
  );

  return {
    fieldError,
    nonFieldErrors,
    isFieldRequired,
    setGeneralError,
  };
}

export default useFormError;
